import clsx from 'clsx';
import React, { useEffect, useId, useRef, useState } from 'react';
import { IconButton, Spinner } from './Button';
import { useEscape } from './Layers';
import { IconCheck, IconChevronDown, IconEye, IconEyeOff } from './icons';

/**
 * Form controls. All controls share one 32px box, 6px radius, hairline border,
 * and a 2px accent focus ring. Validation is communicated by colour AND text.
 */

export const controlBase =
  'w-full rounded-v2 border bg-v2-surface text-v2-13 text-v2-fg transition-colors duration-100 ' +
  'placeholder:text-v2-fg-4 disabled:cursor-not-allowed disabled:bg-v2-sunken disabled:text-v2-fg-3 ' +
  'focus:outline-none focus-visible:outline-none focus:border-v2-accent focus:ring-2 focus:ring-v2-accent/25';

export const borderFor = (invalid?: boolean) =>
  invalid
    ? 'border-v2-danger focus:border-v2-danger focus:ring-v2-danger/25'
    : 'border-v2-line-strong/80 hover:border-v2-line-strong';

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  invalid?: boolean;
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  /** `lg` (40px) is for the search field in a list's header band. */
  size?: 'sm' | 'md' | 'lg';
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ invalid, className, leading, trailing, size = 'md', ...rest }, ref) => {
    const h =
      size === 'sm'
        ? 'h-7'
        : size === 'lg'
          ? 'h-10 rounded-v2-lg text-v2-14'
          : 'h-8';
    const slot = size === 'lg' ? 'w-10' : 'w-8';
    if (!leading && !trailing) {
      return (
        <input
          ref={ref}
          aria-invalid={invalid || undefined}
          className={clsx(
            controlBase,
            borderFor(invalid),
            h,
            'px-2.5',
            className,
          )}
          {...rest}
        />
      );
    }
    return (
      <div className={clsx('relative', className)}>
        {leading && (
          <span
            className={clsx(
              'pointer-events-none absolute inset-y-0 left-0 flex items-center justify-center text-v2-fg-3',
              slot,
            )}
          >
            {leading}
          </span>
        )}
        <input
          ref={ref}
          aria-invalid={invalid || undefined}
          className={clsx(
            controlBase,
            borderFor(invalid),
            h,
            leading ? (size === 'lg' ? 'pl-10' : 'pl-8') : 'pl-2.5',
            trailing ? (size === 'lg' ? 'pr-10' : 'pr-8') : 'pr-2.5',
          )}
          {...rest}
        />
        {trailing && (
          <span
            className={clsx(
              'absolute inset-y-0 right-0 flex items-center justify-center text-v2-fg-3',
              slot,
            )}
          >
            {trailing}
          </span>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';

/**
 * Password field with a show/hide toggle in the trailing slot. The toggle is
 * a named button with `aria-pressed`, out of the tab order so Tab goes from
 * the field to the next field; it never changes the value.
 */
export const PasswordInput = React.forwardRef<
  HTMLInputElement,
  Omit<InputProps, 'type' | 'trailing'>
>((props, ref) => {
  const [shown, setShown] = useState(false);
  return (
    <Input
      ref={ref}
      type={shown ? 'text' : 'password'}
      autoComplete="current-password"
      {...props}
      trailing={
        <IconButton
          size="sm"
          label={shown ? 'Hide password' : 'Show password'}
          aria-pressed={shown}
          tabIndex={-1}
          onClick={() => setShown((s) => !s)}
        >
          {shown ? <IconEyeOff size={14} /> : <IconEye size={14} />}
        </IconButton>
      }
    />
  );
});
PasswordInput.displayName = 'PasswordInput';

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ invalid, className, rows = 3, ...rest }, ref) => (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={clsx(
        controlBase,
        borderFor(invalid),
        'min-h-[64px] resize-y px-2.5 py-1.5 leading-5',
        className,
      )}
      {...rest}
    />
  ),
);
Textarea.displayName = 'Textarea';

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  invalid?: boolean;
  size?: 'sm' | 'md';
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ invalid, className, children, size = 'md', ...rest }, ref) => (
    <div className={clsx('relative', className)}>
      <select
        ref={ref}
        aria-invalid={invalid || undefined}
        className={clsx(
          controlBase,
          borderFor(invalid),
          size === 'sm' ? 'h-7 text-v2-12' : 'h-8',
          'appearance-none py-0 pl-2.5 pr-8 leading-none',
        )}
        {...rest}
      >
        {children}
      </select>
      <IconChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-v2-fg-3" />
    </div>
  ),
);
Select.displayName = 'Select';

export interface ComboboxOption {
  value: string;
  label: string;
}

export interface ComboboxProps {
  options: ComboboxOption[];
  /** The selected option's value; '' when nothing is selected. */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  size?: 'sm' | 'md';
  id?: string;
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

/**
 * Searchable single select. Use it instead of `Select` when the list is long
 * enough that people need to type to find an option. Typing filters by label;
 * arrows move, Enter picks, Escape and blur close without changing the value.
 */
export const Combobox: React.FC<ComboboxProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  disabled,
  invalid,
  size = 'md',
  id,
  className,
  'aria-label': ariaLabel,
  'aria-describedby': describedBy,
}) => {
  const listId = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const selected = options.find((o) => o.value === value);
  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? options.filter((o) => o.label.toLowerCase().includes(needle))
    : options;

  const show = (q = '') => {
    setQuery(q);
    const i = q ? -1 : options.findIndex((o) => o.value === value);
    setActive(Math.max(i, 0));
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setQuery('');
  };
  const pick = (option: ComboboxOption) => {
    onChange(option.value);
    close();
  };

  useEscape(open, close);

  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [open, active]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        show();
        return;
      }
      if (filtered.length === 0) return;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => (i + step + filtered.length) % filtered.length);
    } else if (e.key === 'Enter' && open) {
      e.preventDefault();
      const option = filtered[active];
      if (option) pick(option);
    }
  };

  const activeId = open && filtered[active] ? `${listId}-${active}` : undefined;

  return (
    <div className={clsx('relative', className)}>
      <input
        id={id}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeId}
        aria-invalid={invalid || undefined}
        autoComplete="off"
        disabled={disabled}
        value={open ? query : (selected?.label ?? '')}
        placeholder={open && selected ? selected.label : placeholder}
        onChange={(e) => show(e.target.value)}
        onClick={() => (open ? undefined : show())}
        onKeyDown={onKeyDown}
        onBlur={close}
        className={clsx(
          controlBase,
          borderFor(invalid),
          size === 'sm' ? 'h-7 text-v2-12' : 'h-8',
          'truncate pl-2.5 pr-8',
        )}
      />
      <IconChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-v2-fg-3" />
      {open && (
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={ariaLabel}
          // Keeps focus in the input so choosing an option doesn't blur it.
          onMouseDown={(e) => e.preventDefault()}
          className="v2-fade-in absolute left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto rounded-v2-lg border border-v2-line bg-v2-surface p-1 shadow-v2-float"
        >
          {filtered.length === 0 ? (
            <p className="px-2 py-1.5 text-v2-13 text-v2-fg-3">
              No results found
            </p>
          ) : (
            filtered.map((option, i) => {
              const isSelected = option.value === value;
              return (
                <div
                  key={option.value}
                  id={`${listId}-${i}`}
                  data-index={i}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(option)}
                  className={clsx(
                    'flex cursor-pointer items-center justify-between gap-2 rounded-v2 px-2 py-1.5 text-v2-13',
                    i === active ? 'bg-v2-sunken' : '',
                    isSelected ? 'font-medium text-v2-fg' : 'text-v2-fg-2',
                  )}
                >
                  <span className="min-w-0 truncate">{option.label}</span>
                  {isSelected && (
                    <IconCheck size={14} className="shrink-0 text-v2-accent" />
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  optional?: boolean;
  /** Small adornment rendered after the label text (e.g. a `Tag`). */
  adornment?: React.ReactNode;
  /**
   * A link or button opposite the label ("Forgot password?"). Sits outside
   * the `<label>` so it stays its own target; replaces the Optional word.
   */
  action?: React.ReactNode;
  children: (id: string, describedBy: string | undefined) => React.ReactNode;
  className?: string;
}

/** Label + control + hint/error, wired with ids for assistive tech. */
export const Field: React.FC<FieldProps> = ({
  label,
  hint,
  error,
  optional,
  adornment,
  action,
  children,
  className,
}) => {
  const id = useId();
  // The error replaces the hint, so only the rendered message is referenced.
  const errorId = error ? `${id}-error` : undefined;
  const hintId = !error && hint ? `${id}-hint` : undefined;
  const describedBy = errorId ?? hintId;
  const optionalWord = optional && (
    <span className="font-normal text-v2-fg-4">Optional</span>
  );
  return (
    <div className={clsx('flex flex-col gap-1.5', className)}>
      {action ? (
        <div className="flex items-baseline justify-between gap-3 text-v2-12 font-medium text-v2-fg-2">
          <label htmlFor={id} className="inline-flex items-center gap-1.5">
            {label}
            {adornment}
          </label>
          {action}
        </div>
      ) : (
        <label
          htmlFor={id}
          className="flex items-baseline justify-between text-v2-12 font-medium text-v2-fg-2"
        >
          <span className="inline-flex items-center gap-1.5">
            {label}
            {adornment}
          </span>
          {optionalWord}
        </label>
      )}
      {children(id, describedBy)}
      {error ? (
        <p id={errorId} role="alert" className="text-v2-12 text-v2-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-v2-12 text-v2-fg-3">
          {hint}
        </p>
      ) : null}
    </div>
  );
};

export const Checkbox: React.FC<
  React.InputHTMLAttributes<HTMLInputElement> & { label: string }
> = ({ label, className, ...rest }) => {
  const id = useId();
  return (
    <label
      htmlFor={id}
      className={clsx(
        'inline-flex cursor-pointer items-center gap-2 text-v2-13 text-v2-fg',
        className,
      )}
    >
      <input
        id={id}
        type="checkbox"
        className="h-4 w-4 rounded-v2-sm border-v2-line-strong bg-v2-surface text-v2-accent focus:ring-2 focus:ring-v2-accent/25 focus:ring-offset-0"
        {...rest}
      />
      {label}
    </label>
  );
};

/**
 * Radio: one option of a small, mutually exclusive set that is *saved as a
 * setting* (hours source, default vs custom intro). For switching a data
 * view use `Segmented`; for a long list use `Select`. Wrap the set in an
 * element with `role="radiogroup"` and an `aria-label`.
 */
export const Radio: React.FC<
  React.InputHTMLAttributes<HTMLInputElement> & {
    label: string;
    description?: React.ReactNode;
  }
> = ({ label, description, className, disabled, ...rest }) => {
  const id = useId();
  return (
    <label
      htmlFor={id}
      className={clsx(
        'inline-flex items-start gap-2 text-v2-13 text-v2-fg',
        disabled ? 'cursor-not-allowed text-v2-fg-3' : 'cursor-pointer',
        className,
      )}
    >
      <input
        id={id}
        type="radio"
        disabled={disabled}
        className="mt-0.5 h-4 w-4 shrink-0 border-v2-line-strong bg-v2-surface text-v2-accent focus:ring-2 focus:ring-v2-accent/25 focus:ring-offset-0 disabled:opacity-50"
        {...rest}
      />
      <span className="min-w-0">
        <span className="block font-medium leading-4">{label}</span>
        {description && (
          <span className="mt-0.5 block text-v2-12 leading-[18px] text-v2-fg-3">
            {description}
          </span>
        )}
      </span>
    </label>
  );
};

/**
 * Switch: a boolean setting that saves the moment it is flipped (no Save
 * button, no dirty state). Use it for "this capability is on/off"; use
 * `Checkbox` inside forms that are saved together.
 *
 * - Always paired with a visible word ("On" / "Off") — never colour alone.
 * - `saving` shows a spinner in place of the thumb and blocks a second flip.
 * - `disabledReason` disables it and puts the reason in `title`; pair with a
 *   visible `Reason` nearby, like `Button`.
 * - 28×16 track, 12px thumb — sized to sit on a 20px text line.
 */
export const Switch: React.FC<{
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Accessible name; the visible On/Off word is not the name. */
  label: string;
  saving?: boolean;
  disabledReason?: string;
  /** Hide the On/Off word (only when the row already says what is on). */
  hideWord?: boolean;
  className?: string;
}> = ({
  checked,
  onChange,
  label,
  saving,
  disabledReason,
  hideWord,
  className,
}) => (
  <span className={clsx('inline-flex items-center gap-2', className)}>
    {!hideWord && (
      <span
        className={clsx(
          'text-v2-12',
          checked ? 'font-semibold text-v2-fg' : 'font-medium text-v2-fg-4',
        )}
        aria-hidden
      >
        {checked ? 'On' : 'Off'}
      </span>
    )}
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-busy={saving || undefined}
      title={disabledReason}
      disabled={!!disabledReason}
      // While saving the switch keeps focus (native `disabled` would drop it
      // to <body>, e.g. out of a dialog) but ignores a second flip.
      aria-disabled={saving || undefined}
      onClick={() => {
        if (saving) return;
        onChange(!checked);
      }}
      className={clsx(
        'relative inline-flex h-4 w-7 shrink-0 items-center rounded-full border transition-colors duration-100',
        checked
          ? 'border-v2-accent bg-v2-accent'
          : 'border-v2-line-strong bg-v2-line-strong/60',
        'disabled:cursor-not-allowed disabled:opacity-50',
        saving && 'cursor-progress',
      )}
    >
      <span
        aria-hidden
        className={clsx(
          'absolute top-1/2 flex h-3 w-3 -translate-y-1/2 items-center justify-center rounded-full bg-v2-surface shadow-sm transition-[left] duration-100',
          checked ? 'left-[13px]' : 'left-[1px]',
        )}
      >
        {saving && (
          <Spinner
            className={clsx(
              'h-2.5 w-2.5 border-[1.25px]',
              checked ? 'text-v2-accent' : 'text-v2-fg-3',
            )}
          />
        )}
      </span>
    </button>
  </span>
);
